import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, filter } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  PlImpersonationService,
  PlRoutingService,
  selectImpersonation,
  selectLoadedCurrentUser,
  selectIsPaid,
  UserGroupNames,
} from 'pl-core-services';

import { environment } from '@environments/environment';
import { PL_ROUTING, FeatureFlagName, FeatureFlags } from '@common/constant';
import { BannerService } from '@app/common/services/banner.service';
import { FeatureFlagService } from '@app/common/services/feature-flag.service';
import { LmsService } from '@app/common/services/lms.service';
import { ZendeskAuthService } from '@app/common/services/zendesk-auth.service';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import {
  NavLink,
  HelpMenuLink,
  HelpMenuEmail,
  NavigationComponent,
} from '@presencelearning/ui';
import packageJson from '../../../../package.json';
import { PLLayout } from '../models';

interface NavItem {
  label: string;
  href?: string;
  /**
   * OIDC client ID of the external app `href` points at. When set, the href is
   * built through `PlImpersonationService.buildAppUrl` so the link carries
   * Sirius mode (and the impersonated identity) into the target app.
   */
  oidcClientId?: string;
  routerLink?: string;
  icon?: string;
  target?: string;
  requiredGroups?: UserGroupNames[];
  allowedGroups?: UserGroupNames[];
  featureFlag?: FeatureFlagName;
  divider?: boolean;
  trailingIcon?: string;
}

const ALL_NAV_ITEMS: NavItem[] = [
  {
    label: 'Home',
    icon: 'home',
    routerLink: `/${PL_ROUTING.LANDING_HOME}`,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Student Management',
    icon: 'supervisor_account',
    routerLink: `/${PL_ROUTING.CLIENTS}/management`,
    featureFlag: FeatureFlags.STUDENT_MANAGEMENT,
    allowedGroups: [UserGroupNames.SCHOOL_STAFF_ADMINS],
  },
  {
    label: 'User Management',
    icon: 'manage_accounts',
    routerLink: `/${PL_ROUTING.USER_MANAGEMENT}`,
    allowedGroups: [
      UserGroupNames.SCHOOL_STAFF_ADMINS,
      UserGroupNames.SERVICE_AND_SUPPORT,
      UserGroupNames.ACCOUNT_MANAGERS,
    ],
  },
  {
    label: 'Usage Reports',
    icon: 'assessment',
    routerLink: `/${PL_ROUTING.REPORTS}`,
    allowedGroups: [
      UserGroupNames.SCHOOL_STAFF_ADMINS,
      UserGroupNames.SERVICE_AND_SUPPORT,
      UserGroupNames.ACCOUNT_MANAGERS,
    ],
  },
  {
    label: 'Caseload',
    icon: 'supervisor_account',
    routerLink: `/${PL_ROUTING.CLIENTS}/caseload`,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Schedule',
    icon: 'calendar_month',
    routerLink: `/${PL_ROUTING.CALENDAR}`,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Library',
    icon: 'local_library',
    target: '_blank',
    href: environment.apps.library,
    oidcClientId: environment.room_oidc_client_id,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Room',
    icon: 'desktop_windows',
    target: '_blank',
    href: environment.apps.room,
    oidcClientId: environment.room_oidc_client_id,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    // The Presence root, not a fixed path: edu-clients resolves the Kanga
    // Admin View itself, as PlAdminPresenceRedirectGuard relies on.
    label: 'Admin tools',
    icon: 'dashboard',
    target: '_blank',
    href: environment.apps.presence,
    oidcClientId: environment.presence_oidc_client_id,
    requiredGroups: [
      UserGroupNames.SAAS_PROVIDER,
      UserGroupNames.SCHOOL_STAFF_ADMINS,
    ],
    divider: true,
    trailingIcon: 'open_in_new',
  },
];

const HELP_MENU_LINK_IDS = {
  LMS: 'lms',
  HELP_CENTER: 'help-center',
  SUPPORT_CHAT: 'support-chat',
  TECH_CHECK: 'tech-check',
} as const;

@Component({
  templateUrl: './pl-secure-layout.component.html',
  styleUrls: ['./pl-secure-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlSecureLayoutComponent implements OnInit {
  @ViewChild(NavigationComponent) navigation: NavigationComponent;

  private readonly destroyRef = inject(DestroyRef);

  layout: PLLayout;

  currentUrl = '';

  navPages: NavLink[] = [];

  navResources: NavLink[] = [
    {
      label: 'Facebook Group',
      icon: 'groups',
      href: 'https://www.facebook.com/groups/688197601802713',
      external: true,
    },
  ];

  helpMenuLinks: HelpMenuLink[] = [];

  helpMenuEmails: HelpMenuEmail[] = [];

  version = packageJson.kangaVersion;

  private isPaid = false;

  protected isImpersonating = false;

  private authUserUuid: string | null = null;

  private readonly isSiriusEnabled = getIsSiriusEnabled();

  private isLmsLoading = signal(false);

  readonly helpCenterUrl = 'https://support.presence.com';

  constructor(
    private store: Store,
    private plRouting: PlRoutingService,
    private titleService: Title,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private featureFlagService: FeatureFlagService,
    private lmsService: LmsService,
    private zendeskAuthService: ZendeskAuthService,
    private snackbar: MatSnackBar,
    private impersonationService: PlImpersonationService,
    protected banner: BannerService
  ) {}

  ngOnInit() {
    this.currentUrl = this.router.url;

    this.banner.load();

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
        this.updateActiveLinks();
        this.cdr.markForCheck();
      });

    this.plRouting
      .fullData(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ layout, htmlTitle }) => {
        this.layout = layout;
        let workingHtmlTitle = htmlTitle || this.layout?.title;
        workingHtmlTitle = workingHtmlTitle ? `${workingHtmlTitle} - ` : '';
        this.titleService.setTitle(`${workingHtmlTitle}Kanga`);
      });

    combineLatest([
      this.store.pipe(selectLoadedCurrentUser),
      this.featureFlagService.getAllFeatures(),
      this.store.select(selectImpersonation),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, featureFlags, impersonation]) => {
        this.isImpersonating = impersonation?.isActive ?? false;
        // `targetUserId` is the same auth-db user UUID that was sent to the
        // token exchange, and both are written by the action that flips
        // `isActive` - so it covers tokens that omit the claim.
        this.authUserUuid =
          impersonation?.userData?.auth_db_user_uuid ??
          impersonation?.targetUserId ??
          null;

        const filteredItems = ALL_NAV_ITEMS.filter((item) => {
          if (item.featureFlag && !featureFlags[item.featureFlag]) {
            return false;
          }

          if (item.allowedGroups?.length) {
            return !!item.allowedGroups.find((group) =>
              user?.groups?.includes(group)
            );
          }
          return item.requiredGroups?.every((group) =>
            user?.groups?.includes(group)
          );
        });

        this.navPages = filteredItems.map((item) =>
          this.mapNavItemToNavLink(item)
        );
        this.updateActiveLinks();
        this.cdr.markForCheck();
      });

    this.store
      .select(selectIsPaid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isPaid) => {
        this.isPaid = isPaid;
        this.buildHelpMenu();
        this.cdr.markForCheck();
      });
  }

  onMediaPermissionsHeadToRoom(): void {
    this.banner.dismissMediaPermissions();
    window.location.href = `${environment.apps.room}?use-sirius=1`;
  }

  onMediaPermissionsDismiss(): void {
    this.banner.dismissMediaPermissions();
  }

  onHelpLinkClicked(link: HelpMenuLink): void {
    const linkId = (link as HelpMenuLink & { id?: string }).id;

    if (linkId === HELP_MENU_LINK_IDS.LMS) {
      this.openLmsPortal();
    } else if (linkId === HELP_MENU_LINK_IDS.SUPPORT_CHAT) {
      this.zendeskAuthService.openSupportChat();
    }
  }

  private mapNavItemToNavLink(item: NavItem): NavLink {
    if (item.routerLink) {
      return {
        label: item.label,
        icon: item.icon,
        routerLink: item.routerLink,
        active: this.isLinkActive(item.routerLink),
      };
    }
    return {
      label: item.label,
      icon: item.icon,
      href: item.oidcClientId
        ? this.impersonationService.buildAppUrl(
            item.href,
            item.oidcClientId,
            this.isImpersonating,
            this.authUserUuid
          )
        : item.href,
      external: item.target === '_blank',
      divider: item.divider,
      trailingIcon: item.trailingIcon,
    };
  }

  private isLinkActive(routerLink: string): boolean {
    return this.currentUrl.startsWith(routerLink);
  }

  private updateActiveLinks(): void {
    this.navPages = this.navPages.map((link) => ({
      ...link,
      active: link.routerLink ? this.isLinkActive(link.routerLink) : false,
    }));
  }

  private buildHelpMenu(): void {
    const links: (HelpMenuLink & { id?: string })[] = [];

    if (this.isPaid) {
      links.push({
        id: HELP_MENU_LINK_IDS.LMS,
        icon: 'school',
        title: 'Telehealth Institute',
        subtitle:
          'Become an expert with case studies, practice exercises, and demonstrations',
      });
    }

    links.push(
      {
        id: HELP_MENU_LINK_IDS.HELP_CENTER,
        icon: 'help_center',
        title: 'Help Center',
        subtitle: 'Watch video tutorials and read user guides.',
        href: this.helpCenterUrl,
        external: true,
      },
      {
        id: HELP_MENU_LINK_IDS.SUPPORT_CHAT,
        icon: 'support_agent',
        title: 'Support Chat',
        subtitle: 'Having tech issues? Chat with a real person right now.',
      },
      {
        id: HELP_MENU_LINK_IDS.TECH_CHECK,
        icon: 'laptop',
        title: 'Tech Check',
        subtitle: 'Confirm your computer and equipment are ready to work.',
        href: environment.setup_url,
        external: true,
      }
    );

    this.helpMenuLinks = links;

    const emails: HelpMenuEmail[] = [];
    if (this.isPaid) {
      emails.push({
        label: 'Clinical Support',
        address: 'askaclinician@presence.com',
      });
    }
    emails.push({
      label: 'Technical Support',
      address: 'asksupport@presence.com',
    });

    this.helpMenuEmails = emails;
  }

  private openLmsPortal(): void {
    if (this.isLmsLoading()) {
      return;
    }

    if (!this.isSiriusEnabled) {
      const lmsUrl = `${environment.django_login_url}/saml/sso/learnupon?sp=${environment.learnupon_url}&RelayState=Dashboard`;
      window.open(lmsUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    this.isLmsLoading.set(true);

    this.lmsService
      .openPortal()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLmsLoading.set(false))
      )
      .subscribe({
        error: () => {
          this.snackbar.open('Unable to access Telehealth Institute', 'Close', {
            duration: 5000,
          });
        },
      });
  }
}
